import React, { useState } from 'react';
import styled from 'styled-components';
import Button from 'src/components/form/Button';
import MaterialInput from 'src/components/form/MaterialInput';
import MaterialDatePicker from 'src/components/form/MaterialDatePicker';
import { useFormik } from 'formik';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import * as Yup from 'yup';
import { isValid } from 'date-fns';
import { Poppins, Spacer } from 'src/common';
import { Guard } from './Guard';
import { Modal } from './comps';
import { getDiff } from 'src/utils/getDiff';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import _ from 'lodash';
import AutoGrowingInput from '../form/AutoGrowingInput';
import { useCache } from 'src/graphql';
import { useUpsertAssessmentMutation, useAssessmentsListQuery } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';

const StyledModal = styled(Modal)`
  width: 950px;
  color: ${({ theme }) => theme.colors.cflowerBlue};

  .error {
    color: ${({ theme }) => theme.colors.error};
    text-align: center;
    margin-top: 28px;
  }
`;

const assessmentSchema = Yup.object({
  name: Yup.string().required('Required'),
  reviewDate: Yup.string()
    .nullable()
    .test('reviewDate', 'Invalid Date', (date) => {
      if (!date) return true;
      if (typeof date === 'string') return isValid(new Date(date));
      return false;
    }),
  description: Yup.string().nullable(),
});

interface AssessmentModalProps {
  id?: string;
  onClose: () => void;
}

export const AssessmentModal: React.FC<AssessmentModalProps> = ({ id, onClose }) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [err, setErr] = useState('');
  const { workspaceId } = useAuth();

  const { refetchQuery } = useCache();

  const [upsertAssessment] = useUpsertAssessmentMutation({
    onCompleted: () => {
      refetchQuery({ include: ['assessmentsList'] });
      queryCache.invalidateQueries([QKeys.Audit]);
      onClose();
    },
    onError: (err) => setErr(err?.message || 'Something went wrong'),
  });

  const { data } = useAssessmentsListQuery({ skip: !id });

  const assessment = data?.assessments.find((el) => el.id === id);

  const initialValues = {
    name: assessment?.name || '',
    description: assessment?.description || '',
    reviewDate: (assessment?.reviewDate || '') as string,
    startedDate: (assessment?.startedDate || '') as string,
  };

  const {
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    isSubmitting,
    dirty,
    isValid,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema: assessmentSchema,
    onSubmit: async (values) => {
      setErr('');

      if (!!id) {
        const diff = getDiff(initialValues, { ...values });

        return upsertAssessment({
          variables: {
            assessmentData: { ...diff, id },
          },
        }).then(() => mpEvent(MPEvents.AssessmentUpdate, { id, updatedFields: _.keys(diff), tags: ['ASSESSMENT'] }));
      } else {
        return upsertAssessment({
          variables: {
            assessmentData: values,
            workspaceId,
          },
        }).then(() => mpEvent(MPEvents.AssessmentCreate, { tags: ['ASSESSMENT'] }));
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={() => onClose()}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Poppins className="m-title" px={28}>
            {id ? 'Edit Assessment' : 'Create an Assessment'}
          </Poppins>
          <Spacer $px={20} />
          <AutoGrowingInput
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
            placeholder="Name of assessment *"
            error={touched.name && Boolean(errors.name)}
            helperText={touched.name && errors.name}
            data-cy="assessment-name-input"
            css="width: 100%;"
          />
          <Spacer $px={20} />
          <MaterialInput
            name="description"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            fullWidth
            error={touched.description && Boolean(errors.description)}
            helperText={touched.description && errors.description}
            placeholder="Description of assessment"
            minRows={3}
            maxRows={10}
            data-cy="assessment-description-input"
          />
          <Spacer $px={20} />
          <MaterialDatePicker
            value={values.startedDate || null}
            onChange={(date) => setFieldValue('startedDate', date)}
            inputProps={{
              placeholder: 'Start date',
              fullWidth: true,
              error: touched.startedDate && Boolean(errors.startedDate),
              helperText: touched.startedDate && errors.startedDate,
              onBlur: handleBlur('startedDate'),
              ['data-cy']: 'assessment-started_date-input',
            }}
          />
          <Spacer $px={20} />
          <MaterialDatePicker
            value={values.reviewDate}
            onChange={(date) => setFieldValue('reviewDate', date)}
            inputProps={{
              placeholder: 'Review date',
              fullWidth: true,
              error: touched.reviewDate && Boolean(errors.reviewDate),
              helperText: touched.reviewDate && errors.reviewDate,
              onBlur: handleBlur('reviewDate'),
              ['data-cy']: 'assessment-review_date-input',
            }}
          />

          {err && (
            <Poppins px={14} className="error">
              {err}
            </Poppins>
          )}
          <Spacer $px={30} />
          <Button
            disabled={!isValid || isSubmitting || !dirty}
            onClick={() => {
              handleSubmit();
              if (id) {
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Update Assessment',
                  modal: 'Assessment modal',
                  tags: ['ASSESSMENT'],
                });
              } else {
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Create Assessment',
                  modal: 'Assessment modal',
                  tags: ['ASSESSMENT'],
                });
              }
            }}
            primary
            css="width: 100%;"
            data-cy="assessment-create-button"
          >
            {id ? 'UPDATE' : 'CREATE'}
          </Button>
        </StyledModal>
      }
    />
  );
};
